import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { importCandidatesApi, workspacesApi } from "../services/api";
import { detectShoppingRegion, SHOPPING_REGION_OPTIONS } from "../utils/retailerLinks";
import "../onboarding.css";

const MODULE_OPTIONS = [
  { key: "food",     icon: "🍽", name: "Food & meals",    desc: "Meal planning, pantry, shopping lists",       path: "/app/food" },
  { key: "vault",    icon: "🏠", name: "Home Vault",      desc: "Warranties, appliances, receipts",            path: "/app/home-vault" },
  { key: "vehicles", icon: "🚗", name: "Vehicles",        desc: "Maintenance, insurance, quote checks",        path: "/app/vehicles" },
  { key: "utilities",icon: "⚡", name: "Utilities",       desc: "Bills, usage spikes, savings",                path: "/app/utilities" },
  { key: "subs",     icon: "🔄", name: "Subscriptions",   desc: "Renewals, waste, cancel scripts",             path: "/app/subscriptions" },
  { key: "gifts",    icon: "🎁", name: "People & Gifts",  desc: "Occasions, AI gifts, registries",             path: "/app/people" },
  { key: "trips",    icon: "✈",  name: "Events & Trips",  desc: "Trips, parties, packing, shared plans",       path: "/app/events" },
  { key: "pets",     icon: "🐾", name: "Pets",            desc: "Daily care, food, health, vet records",       path: "/app/pets" },
  { key: "homeCare", icon: "🧰", name: "Home Care",       desc: "Maintenance, repairs, recurring work",       path: "/app/home-care" },
  { key: "property", icon: "🏡", name: "Property Profile",desc: "Home facts, spaces, access, insurance",       path: "/app/property-profile" },
  { key: "safety",   icon: "🛟", name: "Emergency & Safety", desc: "Plans, contacts, controls, readiness",     path: "/app/emergency-safety" },
  { key: "budget",   icon: "💵", name: "Household Budget",desc: "Monthly plan, actuals, household costs",      path: "/app/money/overview" },
];

const MODULE_BRIDGE_CARD = {
  icon: "✦",
  name: "Daily briefing & Timeline",
  desc: "HouseMind connects urgent actions and dated household activity",
};

const US_STATES = [
  ["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"], ["AR", "Arkansas"],
  ["CA", "California"], ["CO", "Colorado"], ["CT", "Connecticut"], ["DE", "Delaware"],
  ["FL", "Florida"], ["GA", "Georgia"], ["HI", "Hawaii"], ["ID", "Idaho"],
  ["IL", "Illinois"], ["IN", "Indiana"], ["IA", "Iowa"], ["KS", "Kansas"],
  ["KY", "Kentucky"], ["LA", "Louisiana"], ["ME", "Maine"], ["MD", "Maryland"],
  ["MA", "Massachusetts"], ["MI", "Michigan"], ["MN", "Minnesota"], ["MS", "Mississippi"],
  ["MO", "Missouri"], ["MT", "Montana"], ["NE", "Nebraska"], ["NV", "Nevada"],
  ["NH", "New Hampshire"], ["NJ", "New Jersey"], ["NM", "New Mexico"], ["NY", "New York"],
  ["NC", "North Carolina"], ["ND", "North Dakota"], ["OH", "Ohio"], ["OK", "Oklahoma"],
  ["OR", "Oregon"], ["PA", "Pennsylvania"], ["RI", "Rhode Island"], ["SC", "South Carolina"],
  ["SD", "South Dakota"], ["TN", "Tennessee"], ["TX", "Texas"], ["UT", "Utah"],
  ["VT", "Vermont"], ["VA", "Virginia"], ["WA", "Washington"], ["WV", "West Virginia"],
  ["WI", "Wisconsin"], ["WY", "Wyoming"], ["DC", "District of Columbia"],
];

const QUICK_WINS = {
  food: [
    { icon: "🥛", title: "Add an expiring item to your pantry",   desc: "Enter something that's about to expire — HouseMind will suggest recipes to use it up tonight.", time: "⚡ 20 seconds" },
    { icon: "🍽", title: "Generate your first 7-day meal plan",   desc: "Set your dietary preferences and let Claude plan your whole week.", time: "⚡ 2 minutes" },
  ],
  vault: [
    { icon: "📺", title: "Add your first appliance",              desc: "Enter your TV, fridge, or washer. HouseMind will track its warranty and maintenance.", time: "⚡ 30 seconds" },
    { icon: "🧾", title: "Scan a receipt",                        desc: "Photograph any recent purchase receipt — HouseMind extracts the item and stores it.", time: "⚡ 15 seconds" },
  ],
  vehicles: [
    { icon: "🚗", title: "Add your car by VIN",                   desc: "Enter your 17-digit VIN and HouseMind auto-fills everything — make, model, recalls, maintenance schedule.", time: "⚡ 30 seconds" },
    { icon: "🔧", title: "Log your last oil change",              desc: "Add your most recent service. HouseMind will tell you when the next one is due.", time: "⚡ 20 seconds" },
  ],
  utilities: [
    { icon: "⚡", title: "Add your electric bill",                 desc: "Enter the provider, amount, due date, and usage. HouseMind starts watching for spikes and due dates.", time: "⚡ 45 seconds" },
    { icon: "💡", title: "Ask where utility costs can improve",    desc: "Add one bill, then ask Utilities AI what changed and where you may save.", time: "⚡ 1 minute" },
  ],
  subs: [
    { icon: "📺", title: "Add your streaming subscriptions",      desc: "Tap to add Netflix, Hulu, Disney+. HouseMind tracks renewal dates and finds duplicates.", time: "⚡ 45 seconds" },
    { icon: "💰", title: "Run your first waste report",           desc: "Add 5 subscriptions and let the AI find waste and duplicate coverage immediately.", time: "⚡ 2 minutes" },
  ],
  gifts: [
    { icon: "🎂", title: "Add someone's upcoming birthday",       desc: "Add a person and their birthday. Get AI gift suggestions and a reminder 3 weeks out.", time: "⚡ 30 seconds" },
    { icon: "🎁", title: "Create a gift registry",                desc: "Create a shareable registry for an upcoming occasion. Share the link with family.", time: "⚡ 2 minutes" },
  ],
  trips: [
    { icon: "✈", title: "Plan an upcoming trip",                  desc: "Enter your destination and dates. Get an AI packing list and activity recommendations instantly.", time: "⚡ 1 minute" },
    { icon: "🎵", title: "Add an upcoming event",                 desc: "Concert, wedding, game night — add it and get gift ideas and what to bring.", time: "⚡ 20 seconds" },
  ],
  pets: [
    { icon: "🐾", title: "Add your pet profile",                   desc: "Save identity, care notes, vet contacts, and the details your household needs every day.", time: "⚡ 1 minute" },
    { icon: "✓", title: "Create today's first care routine",       desc: "Add a feeding, walk, medication, or other routine and choose its days and times.", time: "⚡ 45 seconds" },
  ],
  homeCare: [
    { icon: "🧰", title: "Add one recurring home task",            desc: "Start with a filter, detector, seasonal check, or repair and choose when it is due.", time: "⚡ 45 seconds" },
    { icon: "🔧", title: "Record a provider or completed repair",  desc: "Keep the cost, notes, provider, and completion evidence attached to the work.", time: "⚡ 1 minute" },
  ],
  property: [
    { icon: "🏡", title: "Complete your property basics",          desc: "Add the physical home details and spaces that give other modules the right context.", time: "⚡ 1 minute" },
    { icon: "🛡️", title: "Add or scan your home policy",          desc: "Review policy details before saving them to Property Profile.", time: "⚡ 2 minutes" },
  ],
  safety: [
    { icon: "🛟", title: "Save your household emergency plan",     desc: "Add meeting points and evacuation or shelter instructions your household can find quickly.", time: "⚡ 2 minutes" },
    { icon: "☎️", title: "Add an emergency contact",              desc: "Save a primary or backup contact and make the number available in Emergency Mode.", time: "⚡ 30 seconds" },
  ],
  budget: [
    { icon: "💵", title: "Set one monthly category budget",        desc: "Start with groceries, utilities, subscriptions, gifts, or another household category.", time: "⚡ 30 seconds" },
    { icon: "📊", title: "Review your Money overview",            desc: "See planned amounts beside household spending already recorded in connected modules.", time: "⚡ 1 minute" },
  ],
};

const LEFT_STEPS = [
  { label: "Step 1 of 5 · Welcome",       headline: <>Your household,<br/><em>finally</em><br/>organized.</>,  sub: "One calm place for food, money, property, care, pets, safety, plans, and records." },
  { label: "Step 2 of 5 · Your household", headline: <>Tell us about<br/>your <em>home.</em></>,              sub: "HouseMind personalizes everything to your household — how many people, where you live, and what matters to you." },
  { label: "Step 3 of 5 · Connections", headline: <>Let HouseMind<br/><em>find</em> the<br/>busywork.</>, sub: "Connect read-only sources and HouseMind will build a review inbox. Nothing is saved until you approve it." },
  { label: "Step 4 of 5 · First module",  headline: <>Where do you<br/>want to start<br/><em>first?</em></>,  sub: "Pick the area you want to explore while HouseMind starts organizing connected information." },
  { label: "Step 5 of 5 · First win",     headline: <>Your first<br/><em>win</em> is<br/>already close.</>, sub: "Review what HouseMind finds or add one important item while your household context grows." },
];

const LEFT_PREVIEWS = [
  {
    kicker: "Today",
    title: "3 quiet saves",
    rows: [
      ["$18", "duplicate subscription caught"],
      ["6d", "birthday reminder"],
      ["2", "pantry items expiring"],
    ],
  },
  {
    kicker: "Personalized",
    title: "Built around your household",
    rows: [
      ["City", "local weather and services"],
      ["Bills", "utility due dates and spikes"],
      ["Home", "warranty and appliance context"],
    ],
  },
  {
    kicker: "Automatic discovery",
    title: "Connect once, review safely",
    rows: [["Mail", "bills and renewals"], ["Dates", "trips and appointments"], ["Docs", "warranties and policies"]],
  },
  {
    kicker: "Start here",
    title: "One module, one win",
    rows: [
      ["Pet", "make daily care visible"],
      ["Home", "know what work is due"],
      ["Safety", "keep the plan findable"],
    ],
  },
  {
    kicker: "First win",
    title: "HouseMind starts remembering",
    rows: [
      ["30s", "add one important item"],
      ["AI", "get useful next steps"],
      ["Done", "daily briefing updates"],
    ],
  },
];

export default function WelcomePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, setShoppingRegion } = useAuth();
  const firstName = user?.first_name || user?.name?.split(" ")[0] || "";

  const initialStep = Math.min(5, Math.max(1, Number(searchParams.get("step")) || 1));
  const [step, setStep]               = useState(initialStep);
  const [form, setForm]               = useState({ firstName, region: detectShoppingRegion(), city: "", state: "", homeType: "" });
  const [householdSize, setSize]      = useState("");
  const [selectedModule, setModule]   = useState("");
  const [selectedQW, setQW]           = useState(null);
  const [savingHomeType, setSavingHomeType] = useState(false);
  const [connections, setConnections] = useState([]);
  const [connectionsLoading, setConnectionsLoading] = useState(false);
  const [connectionBusy, setConnectionBusy] = useState(null);
  const [connectionNotice, setConnectionNotice] = useState("");

  useEffect(() => {
    if (step === 5) {
      setQW(0);
    }
  }, [step]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [step]);

  const goTo = (s) => { setStep(s); };

  const loadConnections = async () => {
    setConnectionsLoading(true);
    try {
      const data = await importCandidatesApi.sources();
      setConnections((data?.sources || []).filter(row => ["gmail", "google_calendar", "google_drive", "outlook", "outlook_calendar"].includes(row.provider)));
    } catch {
      setConnectionNotice("Connections are temporarily unavailable. You can finish this later from HouseMind.");
    } finally { setConnectionsLoading(false); }
  };

  useEffect(() => {
    if (step === 3) loadConnections();
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (searchParams.get("connector") !== "setup") return;
    const status = searchParams.get("status");
    const connectionId = Number(searchParams.get("connection_id"));
    const shouldRunFirstSync = status === "connected" && searchParams.get("first_sync") === "1" && connectionId > 0;
    setStep(3);
    setConnectionNotice(status === "connected" ? (shouldRunFirstSync ? "Connection complete. HouseMind is running the first scan now…" : "Connection complete. Add another source or continue.") : "That connection did not complete. You can retry or continue for now.");
    const next = new URLSearchParams(searchParams);
    next.delete("connector"); next.delete("status"); next.delete("connection_id"); next.delete("first_sync");
    setSearchParams(next, { replace: true });
    if (shouldRunFirstSync) {
      setConnectionBusy(connectionId);
      importCandidatesApi.syncConnection(connectionId).then(result => {
        const stats = result?.stats || {};
        const checked = Number(stats.adapter_checked || 0);
        const created = Number(stats.adapter_created || 0);
        setConnectionNotice(`First scan complete: ${checked} item${checked === 1 ? "" : "s"} checked and ${created} new review item${created === 1 ? "" : "s"} found.`);
        loadConnections();
      }).catch(error => {
        setConnectionNotice(error?.message || "Gmail is connected, but the first scan could not finish. Retry from Connections.");
        loadConnections();
      }).finally(() => setConnectionBusy(null));
    }
  }, [searchParams, setSearchParams]);

  const connectSource = async (source) => {
    setConnectionBusy(source.id); setConnectionNotice("");
    try {
      const result = await importCandidatesApi.requestConnection(source.id, { return_to: "/welcome?step=3" });
      if (result?.oauth?.ready && result.oauth.auth_url) window.location.assign(result.oauth.auth_url);
      else setConnectionNotice(result?.oauth?.message || "This connection is not configured yet. You can continue and finish it later.");
    } catch (error) { setConnectionNotice(error?.message || "HouseMind could not start this connection."); }
    finally { setConnectionBusy(null); }
  };

  const modulePath = MODULE_OPTIONS.find(m => m.key === selectedModule)?.path || "/app";
  const moduleName = MODULE_OPTIONS.find(m => m.key === selectedModule)?.name || "HouseMind";

  const propertyType = form.homeType === "renter" ? "renter" : "owner";

  const saveHouseholdPrefs = async () => {
    setShoppingRegion(form.region || detectShoppingRegion());
    setSavingHomeType(true);
    try {
      await workspacesApi.updateCurrent({
        property_type: propertyType,
        first_name: form.firstName,
        city: form.city,
        state: form.state,
        region: form.region,
        household_size: householdSize,
      });
    } catch {
      // Onboarding should not block if this fails; user can update in Settings later.
    } finally {
      setSavingHomeType(false);
    }
  };

  const continueFromHousehold = async () => {
    await saveHouseholdPrefs();
    goTo(3);
  };

  const pickModule = key => {
    setModule(key);
    goTo(5);
  };

  const goToDashboard = async () => {
    await saveHouseholdPrefs();
    navigate(selectedModule ? modulePath : "/app");
  };

  const skipToDashboard = async () => {
    await saveHouseholdPrefs();
    navigate("/app");
  };

  const left = LEFT_STEPS[step - 1];
  const preview = LEFT_PREVIEWS[step - 1];

  return (
    <div className={`ob-wrap ob-wrap--step-${step}`}>
      {/* ── LEFT PANEL ── */}
      <div className="ob-left">
        <div className="ob-logo">House<em>Mind</em></div>
        <div className="ob-preview-stage" aria-hidden="true">
          <div className="ob-photo-card">
            <div className="ob-photo-shine" />
            <div className="ob-mini-briefing">
              <span>{preview.kicker}</span>
              <strong>{preview.title}</strong>
            </div>
          </div>
          <div className="ob-floating-card">
            {preview.rows.map(([value, label]) => (
              <div className="ob-floating-row" key={`${value}-${label}`}>
                <strong>{value}</strong>
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="ob-hero-text">
          <div className="ob-step-label">{left.label}</div>
          <div className="ob-headline">{left.headline}</div>
          <div className="ob-sub">{left.sub}</div>
          <div className="ob-progress">
            {[1,2,3,4,5].map(i => (
              <div
                key={i}
                className={`ob-dot${i === step ? " ob-dot--active" : i < step ? " ob-dot--done" : ""}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ── RIGHT PANEL ── */}
      <div className="ob-right">
        <div className="ob-mobile-top">
          <div className="ob-mobile-top-row">
            <div className="ob-mobile-brand">House<em>Mind</em></div>
          </div>
          <div className="ob-mobile-step">{left.label}</div>
          <div className="ob-mobile-progress" aria-hidden="true">
            {[1,2,3,4,5].map(i => (
              <span
                key={i}
                className={i === step ? "is-active" : i < step ? "is-done" : ""}
              />
            ))}
          </div>
        </div>
        <div className={`ob-mobile-intro${step > 1 ? " ob-mobile-intro--compact" : ""}`} key={`mobile-intro-${step}`}>
          <div className="ob-mobile-copy">
            <div className="ob-step-label">{left.label}</div>
            <div className="ob-mobile-headline">{left.headline}</div>
            <div className="ob-mobile-sub">{left.sub}</div>
          </div>
          <div className="ob-mobile-preview" aria-hidden="true">
            <div className="ob-mobile-preview-head">
              <span>{preview.kicker}</span>
              <strong>{preview.title}</strong>
            </div>
            <div className="ob-mobile-preview-rows">
              {preview.rows.map(([value, label]) => (
                <div className="ob-mobile-preview-row" key={`${value}-${label}`}>
                  <strong>{value}</strong>
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="ob-content">

          {/* STEP 1 — Welcome */}
          {step === 1 && (
            <div className="ob-screen ob-screen--step1">
              <div className="ob-eyebrow">✦ You're in — account created</div>
              <h2>Welcome to HouseMind.</h2>
              <p>Let's get your household set up in 3 quick steps. Connected household areas, one calm daily briefing and Timeline.</p>
              <div className="ob-feature-list">
                {MODULE_OPTIONS.map(f => (
                  <div className="ob-wf-item" key={f.name}>
                    <div className="ob-wf-icon">{f.icon}</div>
                    <div><strong>{f.name}</strong>{f.desc}</div>
                  </div>
                ))}
                <div className="ob-wf-item ob-wf-item--bridge">
                  <div className="ob-wf-icon">{MODULE_BRIDGE_CARD.icon}</div>
                  <div><strong>{MODULE_BRIDGE_CARD.name}</strong>{MODULE_BRIDGE_CARD.desc}</div>
                </div>
              </div>
              <div className="ob-safety-box">
                <div className="ob-safety-title">🛡 Safety gates — always on</div>
                {[
                  "13+ account gate — COPPA compliant",
                  "Allergy hard-filter — food safety guaranteed",
                  "Private by default — share only what you choose",
                ].map(s => (
                  <div className="ob-safety-item" key={s}>
                    <div className="ob-safety-check">✓</div>
                    <span>{s}</span>
                  </div>
                ))}
              </div>
              <button className="ob-btn-primary" onClick={() => goTo(2)}>Let's set up my household →</button>
              <button className="ob-btn-secondary" onClick={skipToDashboard}>Skip setup and go to dashboard</button>
            </div>
          )}

          {/* STEP 2 — Household */}
          {step === 2 && (
            <div className="ob-screen">
              <button className="ob-btn-back" onClick={() => goTo(1)}>← Back</button>
              <h2>About your household</h2>
              <p>This helps HouseMind personalize your daily briefing and module suggestions.</p>

              <div className="ob-form-group">
                <label className="ob-form-label">What should HouseMind call you?</label>
                <input className="ob-form-input" placeholder="John" value={form.firstName}
                  onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} />
              </div>

              <div className="ob-form-group">
                <label className="ob-form-label">Shopping region</label>
                <select className="ob-form-select" value={form.region}
                  onChange={e => setForm(f => ({ ...f, region: e.target.value, state: e.target.value === "US" ? f.state : "" }))}>
                  {SHOPPING_REGION_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <div className="ob-form-hint">Detected from your browser when possible. You can change this later in Settings.</div>
              </div>

              <div className="ob-form-group">
                <label className="ob-form-label">Where are you located?</label>
                <div className="ob-form-row">
                  <input className="ob-form-input" placeholder="City" value={form.city}
                    onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
                  {form.region === "US" ? (
                    <select className="ob-form-select" value={form.state}
                      onChange={e => setForm(f => ({ ...f, state: e.target.value }))}>
                      <option value="">Select state</option>
                      {US_STATES.map(([abbr, name]) => (
                        <option key={abbr} value={abbr}>{abbr} - {name}</option>
                      ))}
                    </select>
                  ) : (
                    <input className="ob-form-input" placeholder="Region / province" value={form.state}
                      onChange={e => setForm(f => ({ ...f, state: e.target.value }))} />
                  )}
                </div>
                <div className="ob-form-hint">Used for local service recommendations and weather-aware meal planning</div>
              </div>

              <div className="ob-form-group">
                <label className="ob-form-label">Household size</label>
                <div className="ob-size-grid">
                  {[["Just me","1"],["2 people","2"],["3–4 people","3-4"],["5+ people","5+"]].map(([label, val]) => (
                    <button key={val}
                      className={`ob-size-opt${householdSize === val ? " ob-size-opt--selected" : ""}`}
                      onClick={() => setSize(val)}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="ob-form-group">
                <label className="ob-form-label">Do you own or rent your home?</label>
                <div className="ob-size-grid">
                  {[["Own my home", "owner"], ["Rent / lease", "renter"]].map(([label, val]) => (
                    <button key={val}
                      type="button"
                      className={`ob-size-opt${propertyType === val ? " ob-size-opt--selected" : ""}`}
                      onClick={() => setForm(f => ({ ...f, homeType: val }))}>
                      {label}
                    </button>
                  ))}
                </div>
                <div className="ob-form-hint">Affects which Home Vault features are most relevant to you</div>
              </div>

              <div className="ob-age-gate">
                <span className="ob-age-gate-icon">⚠</span>
                <span>By continuing you confirm you are 13 years of age or older. HouseMind does not knowingly collect data from children under 13.</span>
              </div>

              <div className="ob-nav-actions">
                <button type="button" className="ob-nav-back" onClick={() => goTo(1)}>Back</button>
                <button className="ob-btn-primary" onClick={continueFromHousehold} disabled={savingHomeType}>{savingHomeType ? "Saving..." : "Continue →"}</button>
              </div>
            </div>
          )}

          {/* STEP 3 — Connection-first discovery */}
          {step === 3 && (
            <div className="ob-screen">
              <button className="ob-btn-back" onClick={() => goTo(2)}>← Back</button>
              <div className="ob-eyebrow">✦ Review before anything is saved</div>
              <h2>Start with what you already have.</h2>
              <p>Connect any source you trust. HouseMind uses read-only access to find possible household records and asks before updating a module.</p>
              <div className="ob-connection-summary">
                <strong>{connections.filter(row => row.permission_status === "connected").length} connected</strong>
                <span>Bills · renewals · trips · warranties · appointments</span>
              </div>
              {connectionNotice && <div className="ob-connection-notice">{connectionNotice}</div>}
              <div className="ob-connection-list">
                {connectionsLoading && connections.length === 0 ? <p>Checking available connections…</p> : connections.map(source => {
                  const connected = source.permission_status === "connected";
                  return <article key={source.id} className={connected ? "is-connected" : ""}>
                    <div className="ob-connection-icon">{source.provider.includes("google") || source.provider === "gmail" ? "G" : "O"}</div>
                    <div><strong>{source.label.replace(" connector", "")}</strong><span>{source.source_account || (source.provider.includes("calendar") ? "Dates and commitments" : source.provider.includes("drive") ? "Warranties and documents" : "Bills, receipts, and renewals")}</span></div>
                    {connected ? <b>✓ Connected</b> : <button type="button" disabled={connectionBusy === source.id} onClick={() => connectSource(source)}>{connectionBusy === source.id ? "Opening…" : "Connect"}</button>}
                  </article>;
                })}
              </div>
              <div className="ob-connection-safety"><b>Your approval is the write permission.</b><span>Connecting lets HouseMind read eligible information. It does not create subscriptions, bills, events, or Vault records until you confirm a Discovery.</span></div>
              <div className="ob-nav-actions">
                <button type="button" className="ob-nav-back" onClick={() => goTo(2)}>Back</button>
                <button type="button" className="ob-btn-primary" onClick={() => goTo(4)}>Continue →</button>
              </div>
            </div>
          )}

          {/* STEP 4 — Pick module */}
          {step === 4 && (
            <div className="ob-screen">
              <button className="ob-btn-back" onClick={() => goTo(3)}>← Back</button>
              <h2>What matters most<br/>to you right now?</h2>
              <p>Pick one area to explore first. Connected discoveries can update supported household records after your approval.</p>
              <div className="ob-module-grid">
                {MODULE_OPTIONS.map(m => (
                  <button
                    type="button"
                    key={m.key}
                    className={`ob-mod-option${selectedModule === m.key ? " ob-mod-option--selected" : ""}`}
                    onClick={() => pickModule(m.key)}
                  >
                    <span className="ob-mod-icon">{m.icon}</span>
                    <div className="ob-mod-name">{m.name}</div>
                    <div className="ob-mod-desc">{m.desc}</div>
                  </button>
                ))}
                <div className="ob-mod-option ob-mod-option--bridge" aria-hidden="true">
                  <span className="ob-mod-icon">{MODULE_BRIDGE_CARD.icon}</span>
                  <div className="ob-mod-name">{MODULE_BRIDGE_CARD.name}</div>
                  <div className="ob-mod-desc">{MODULE_BRIDGE_CARD.desc}</div>
                </div>
              </div>
              <div className="ob-nav-actions">
                <button type="button" className="ob-nav-back" onClick={() => goTo(3)}>Back</button>
                <button
                  type="button"
                  className="ob-btn-primary"
                  onClick={() => goTo(5)}
                >
                  Continue →
                </button>
              </div>
            </div>
          )}

          {/* STEP 5 — Quick win */}
          {step === 5 && (
            <div className="ob-screen ob-screen--step4">
              <button className="ob-btn-back" onClick={() => goTo(4)}>← Back</button>
              <h2>Your first quick win</h2>
              <p>
                {selectedModule
                  ? `Great choice. Here's your fastest first win in ${moduleName}:`
                  : "Add one item to get HouseMind working for you immediately."}
              </p>
              <div className="ob-qw-list">
                {(QUICK_WINS[selectedModule || "food"]).map((qw, i) => (
                  <div
                    key={i}
                    className={`ob-qw-card${selectedQW === i ? " ob-qw-card--selected" : ""}`}
                    onClick={() => setQW(i)}
                  >
                    <div className="ob-qw-icon">{qw.icon}</div>
                    <div>
                      <div className="ob-qw-title">{qw.title}</div>
                      <div className="ob-qw-desc">{qw.desc}</div>
                      <div className="ob-qw-time">{qw.time}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="ob-qw-next-card" aria-label="What happens after setup">
                <div className="ob-qw-next-card__icon">✦</div>
                <div>
                  <div className="ob-qw-next-card__title">After this, your dashboard starts with context.</div>
                  <div className="ob-qw-next-card__body">
                    HouseMind uses your first saved item to shape the daily briefing and module Ask AI answers.
                  </div>
                  <div className="ob-qw-next-list" aria-label="What HouseMind will do next">
                    {[
                      "Add it to your module",
                      "Watch for deadlines, waste, or reminders",
                      "Include it in tomorrow's briefing",
                    ].map((item, i) => (
                      <div className="ob-qw-next-list__item" key={item}>
                        <span>{i + 1}</span>
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="ob-step4-actions">
                <div className="ob-nav-actions">
                  <button type="button" className="ob-nav-back" onClick={() => goTo(4)}>Back</button>
                  <button className="ob-btn-primary" onClick={goToDashboard}>
                    {selectedModule
                      ? `Start with ${MODULE_OPTIONS.find(
                          m => m.key === selectedModule
                        )?.name} →`
                      : "Take me to my dashboard →"
                    }
                  </button>
                </div>
                <button className="ob-btn-secondary" onClick={skipToDashboard}>
                  Skip for now — I'll explore on my own
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
